/**
 * Japanese translations
 */
import type { TranslationKeys } from '../utils/i18n';

export const ja: TranslationKeys = {
  controls: {
    maxTime: '最大時間',
    maxTimeMinutes: '最大時間（分）',
    travelMode: '移動手段',
    speed: '速度',
    mapStyle: '地図スタイル',
    language: '言語',
    share: '共有',
    play: '再生',
    pause: '一時停止',
    restart: '再開',
    loading: '読み込み中...',
    showControls: 'コントロールを表示',
    hideControls: 'コントロールを非表示',
    expandControls: 'コントロールを展開',
    collapseControls: 'コントロールを折りたたむ',
    selectLocationHint: '地図上で位置を選択して開始してください',
  },
  travelModes: {
    driving: '車',
    walking: '徒歩',
    cycling: '自転車',
    transit: '公共交通機関',
  },
  speed: {
    verySlow: '0.1x（非常に遅い）',
    slow: '0.25x（遅い）',
    half: '0.5x（半分）',
    normal: '1x（通常）',
    fast: '1.5x（速い）',
    veryFast: '2x（非常に速い）',
  },
  mapStyles: {
    streets: 'ストリート',
    satellite: '衛星',
  },
  languages: {
    en: 'English',
    'zh-Hans': '简体中文 (Simplified Chinese)',
    'zh-Hant': '繁體中文 (Traditional Chinese)',
    es: 'Español (Spanish)',
    fr: 'Français (French)',
    de: 'Deutsch (German)',
    ja: '日本語 (Japanese)',
    ko: '한국어 (Korean)',
  },
  addressInput: {
    label: '住所を検索',
    placeholder: '住所を入力して時間マップを計算',
    noResults: '結果が見つかりません',
    loading: '提案を読み込み中',
  },
  error: {
    retry: '再試行',
    dismiss: 'エラーを閉じる',
    somethingWentWrong: '問題が発生しました。ページを更新してください。',
    browserNotSupported: 'お使いのブラウザはこのアプリケーションをサポートしていません。モダンブラウザをご使用ください。',
    usageLimitReached: 'ベータ版の制限である5回の計算に達しました。お試しいただきありがとうございます！このプロジェクトに興味がある方はDMをください。',
  },
  progress: {
    minutes: '分',
  },
  share: {
    urlCopied: 'URLがクリップボードにコピーされました！',
    shareUrl: 'URLを共有',
  },
  emailGate: {
    title: '時間マップ Betaにサインイン',
    subtitle: 'メールアドレスを入力してログインリンクを受け取る',
    emailPlaceholder: 'your.email@example.com',
    emailLabel: 'メールアドレス',
    sendButton: 'ログインリンクを送信',
    sendingButton: 'ログインリンクを送信中...',
    checkEmailTitle: 'メールを確認してください',
    checkEmailMessage: '{email} にログインリンクを送信しました',
    checkEmailInstruction: 'メール内のリンクをクリックして、時間マップに続行します。',
    checkEmailHelp: 'メールが届いていませんか？スパムフォルダを確認するか、別のメールアドレスで再試行してください。',
    emailRequired: 'メールアドレスを入力してください',
    emailInvalid: '有効なメールアドレスを入力してください',
    domainRestriction: 'ベータアクセスは、@gmail.com、@outlook.com、@qq.comのメールアドレスのみ許可されています。',
    domainNotAllowed: 'ベータアクセスは現在、Gmail、Outlook、QQのメールアドレスに限定されています',
    typoSuggestion: '@{domain}の間違いではありませんか？',
    rateLimitExceeded: 'リクエストが多すぎます。しばらく待ってから再試行してください。',
    invalidEmailFormat: 'メールアドレスが無効です。確認して再試行してください。',
    failedToSend: 'ログインリンクの送信に失敗しました。もう一度お試しください。',
    magicLinkFailed: 'マジックリンクの送信に失敗しました：{error}',
    testMeButton: 'テスト',
    bannerText: '時間マップ',
    betaTag: 'ベータ',
  },
  testCode: {
    title: 'テストコードを入力',
    subtitle: 'テストコードをお持ちですか？以下に入力して開始してください',
    placeholder: 'コードを入力',
    button: 'コードで続行',
    haveCodeLink: 'テストコードをお持ちですか？',
    useEmailLink: '代わりにメールを使用',
    invalid: '無効なテストコード',
    expired: 'このテストコードは期限切れです',
    limitReached: 'このテストコードは使用制限に達しました',
    optionalEmailTitle: '進行状況を保存（オプション）',
    optionalEmailSubtitle: '作業を保存しますか？以下にメールアドレスを入力してください（またはスキップ）',
    optionalEmailPlaceholder: 'your.email@example.com（オプション）',
    skipButton: '今はスキップ',
    submitEmailButton: '続行',
  },
  usageIndicator: {
    template: '残り{count}回の計算',
    loading: '読み込み中...',
  },
  disclaimer: {
    loading: '読み込み中...',
    title: '重要な免責事項',
    content: 'これはベータ版および実験的なプロジェクトです。すべての地図データは、サードパーティサービスであるMapboxによって提供されており、独立して検証されていません。地図には不正確な情報が含まれている可能性があり、重要な意思決定には使用しないでください。移動時間の計算は推定値のみであり、正確性は検証されていません。本アプリケーションの使用から生じる不正確さ、エラー、または結果について、一切の責任を負いません。',
    consent: '本アプリケーションを引き続き使用することにより、上記のすべての条件および免責事項を認識し、同意したものとみなされます。',
  },
  intro: {
    title: 'あなたの到達範囲を発見',
    message: '任意の場所から指定時間内にどこまで移動できるかを視覚化します。\n\n地図上でアニメーション化された移動時間到達範囲を見て、住む場所、働く場所、訪れる場所についてより良い決定を下しましょう。',
    step1: '地図を検索またはクリックして出発点を設定します',
    step2: '地図はアニメーションゾーンで移動時間到達範囲を表示します',
    step3: '移動時間と交通手段を設定します',
    step4: '再生をクリックして、時間の経過とともに到達範囲が拡大する様子を見ます',
    step5: '共有可能なリンクで他の人と視覚化を共有します',
    next: '次へ',
    skip: 'ツアーをスキップ',
    gotIt: '了解！',
    startTour: 'ツアーを開始',
  },
};

